import { Paperclip, X } from "lucide-react";
import Loading from "../../shared/Loading";
import CustomInput from "../../shared/CustomInput";
import { useQuery } from "react-query";
import { getLoanOfficers } from "../../sdk/users/users";

const ReassignOfficer = ({
  closeReassignOfficer,
  handleCollectDetails,
  loading,
  reassignDetails,
  setReassignDetails,
}) => {
  const { data: loanOfficers } = useQuery({
    queryKey: ["loan officers"],
    queryFn: async () => {
      const response = await getLoanOfficers();
      return response.data.data.items;
    },
  });

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setReassignDetails((prev) => ({
      ...prev,
      files,
    }));
  };

  return (
    <div
      onClick={closeReassignOfficer}
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[850px] h-[600px] rounded-[10px] p-[15px] relative bg-white"
      >
        {/* Close Button */}
        <div
          onClick={closeReassignOfficer}
          className="absolute top-[0px] cursor-pointer h-[40px] w-[40px] bg-[#D63B3B] rounded-tr-[10px] rounded-bl-[20px] right-[0px] flex items-center justify-center"
        >
          <X color="#ffffff" />
        </div>

        {/* Next Button */}
        <div className="absolute bottom-[20px] right-[20px]">
          <button
            disabled={loading}
            onClick={handleCollectDetails}
            className="h-[35px] bg-[#000000] flex items-center justify-center gap-[5px] rounded-[30px] px-[15px] text-[15px] font-bold text-white"
          >
            {loading && <Loading />}
            Next
          </button>
        </div>

        {/* Content */}
        <div>
          <p className="text-[#000000] text-[20px] font-semibold">
            Reassign Loan Officer
          </p>
          <p className="text-[#5C5C5C] font-normal text-[14px]">
            Choose a different loan officer to manage this loan. The current
            officer will be unlinked once reassignment is confirmed.
          </p>

          {/* Officers */}
          <div className="flex items-start my-[20px] justify-between">
            <div className="flex w-[47%] flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Current Loan Officer
              </label>
              <CustomInput
                placeholder="Current loan Officer"
                type="text"
                value={reassignDetails?.currentOfficer || ""}
                onChange={(e) =>
                  setReassignDetails((prev) => ({
                    ...prev,
                    currentOfficer: e.target.value,
                  }))
                }
              />
            </div>
            <div className="flex w-[47%] flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Select New Loan Officer
              </label>
              <select
                className="h-[45px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-[#D9D9D9] bg-white"
                name="Select New Loan Officer"
                value={reassignDetails?.newOfficerId}
                onChange={(e) => {
                  const selectedOfficer = loanOfficers.find(
                    (officer) => officer.id === e.target.value,
                  );
                  setReassignDetails((prev) => ({
                    ...prev,
                    newOfficerId: e.target.value,
                    newOfficerName: selectedOfficer
                      ? `${selectedOfficer.firstname} ${selectedOfficer.lastname}`
                      : "",
                  }));
                }}
              >
                <option value="">Select an officer</option>
                {loanOfficers?.map((option) => (
                  <option key={option?.id} value={option?.id}>
                    {option?.firstname} {option?.lastname}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Reason */}
          <div className="flex w-full mb-[20px] flex-col">
            <label className="text-[#222222] font-medium text-[13px]">
              Reason to reassign officer (Optional)
            </label>
            <textarea
              className="h-[95px] w-full text-[14px] rounded-[5px] px-[10px] border border-[#D9D9D9] focus:outline-none"
              value={reassignDetails?.reason || ""}
              placeholder="Enter a reason to reassign the officer"
              onChange={(e) =>
                setReassignDetails((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="text-[#222222] font-medium text-[13px]">
              Additional document(s)
            </label>

            {/* Hidden Input */}
            <input
              id="file-upload"
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Upload Button */}
            <label htmlFor="file-upload" className="cursor-pointer">
              <div className="h-[45px] rounded-[5px] w-full px-[15px] gap-[10px] border border-[#000000] flex items-center">
                <Paperclip size={16} color="#000000" />
                <p className="text-[#000000] text-[16px] font-normal">
                  Attach file(s) (.pdf, .docx, .doc, .jpeg, .png)
                </p>
              </div>
            </label>

            {/* File List */}
            {/* File List */}
            {reassignDetails?.files?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {reassignDetails.files.map((file, index) => (
                  <div
                    key={index}
                    className="relative w-full px-[20px] py-[6px] rounded-[6px] bg-[#F9F9F9] border border-[#DDD] text-center"
                  >
                    {/* File Name */}
                    <p className="text-[12px] font-semibold text-black truncate">
                      {file.name}
                    </p>
                    {/* File Size */}
                    <p className="text-[11px] text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>

                    {/* Remove File Button */}
                    <button
                      onClick={() =>
                        setReassignDetails((prev) => ({
                          ...prev,
                          files: prev.files.filter((_, i) => i !== index),
                        }))
                      }
                      className="absolute top-1/2 right-1 -translate-y-1/2 text-gray-400 hover:text-red-500"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignOfficer;
